import { css } from '@emotion/core';
import { Page } from '../../shared';
import { theme } from '@weave/theme';
import { SendUserInviteAction } from './SendUserInviteAction';
import { SentInviteAuditLogs } from './SentInviteAuditLogs';
import { OnboardingActivity } from './OnboardingActivity';
import { useSelector } from 'react-redux';
import { selectCurrentLocationV2 } from '../../../redux/actions/location';
import { useGetLocationHierarchy } from '../onboarding.queries';
import { CPQQuoteType } from '@weave/schema-gen-ts/dist/schemas/salesforce/v1/salesforce.pb';
import { ContentLoader, Heading, PrimaryButton, Tabs } from '@weave/design-system';
import { useAlert } from '@weave/alert-system';
import React, { useEffect } from 'react';
import { SendUserInviteEmailAction } from './SendUserInviteEmailAction';

export const FastPassPage = () => {
  const currentLocation = useSelector(selectCurrentLocationV2);
  const locationId = currentLocation?.locationId;
  const alerts = useAlert();

  const {
    data: locationOnboardingData,
    isLoading,
    refetch,
    isError,
  } = useGetLocationHierarchy(
    {
      locationId,
    },
    {
      retry: false,
      select: (response) => response.locations?.[0],
    }
  );

  const isQuoteTypeEvent = locationOnboardingData?.quoteType === CPQQuoteType.EVENT;

  useEffect(() => {
    if (isError) {
      alerts.error('Failed to load location hierarchy');
    }
  }, [isError]);

  return (
    <Page
      title="Fast Pass"
      subtitle={
        isQuoteTypeEvent ? 'Send the onboarding intake form and view email history' : ''
      }
    >
      <section css={styles.card}>
        {isLoading && <ContentLoader show />}
        {isError ? (
          <div css={styles.warningBanner}>
            <Heading level={3}>Failed to load location hierarchy</Heading>
            <PrimaryButton css={{ maxWidth: 'max-content' }} onClick={() => refetch()}>
              Retry
            </PrimaryButton>
          </div>
        ) : isQuoteTypeEvent ? (
          <Tabs initialTab="invite">
            <Tabs.Bar>
              <Tabs.Tab id="invite" controls="invite-panel">
                Invite Actions
              </Tabs.Tab>
              <Tabs.Tab id="activity" controls="activity-panel">
                Activity
              </Tabs.Tab>
            </Tabs.Bar>
            <Tabs.Panel id="invite-panel" controller="invite">
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(3),
                  marginTop: theme.spacing(2),
                }}
              >
                <SendUserInviteAction />
                <SendUserInviteEmailAction />
                <SentInviteAuditLogs />
              </div>
            </Tabs.Panel>
            <Tabs.Panel id="activity-panel" controller="activity">
              <div css={{ marginTop: theme.spacing(2) }}>
                <OnboardingActivity showUserInviteAction={false} />
              </div>
            </Tabs.Panel>
          </Tabs>
        ) : (
          <OnboardingActivity />
        )}
      </section>
    </Page>
  );
};

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    padding: ${theme.spacing(3)};
    border-radius: ${theme.borderRadius.medium};
    box-shadow: ${theme.shadows.light};
    position: relative;
  `,
  warningBanner: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${theme.colors.critical10};
    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.critical50};
    padding: ${theme.spacing(2)};
  `,
};
